import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { fetchSearchSupportIssues } from './queries/fetch-search-support-issues';
import { Link } from 'react-router-dom';
import { FormattedDate, DynamicResultTable, SimpleTD, DateFilter } from '@css-xp/base';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE } from '@atlassian/cssxp-utils-entitlement';
import {
  DateRange,
  FetchSearchSupportIssuesQuery,
  FetchSearchSupportIssuesQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
// @ts-ignore
import SupportIssueIcon from '@css-xp/base/assets/img/icon-support-issue.svg';
import { NEW_DESIGN_DATE_FORMAT, SUPPORT_ISSUE_PATH } from '@atlassian/cssxp-typescommon';
import './support-issues-cloud.scss';
import { SupportIssueSelectedStatuses, SupportIssueStatusFilter } from './support-issue-status-filter';
import SupportIssueStatusLozenge from './support-issue-status-lozenge';
import { SupportIssueStatusEnum, SupportIssueSearchParams } from '@atlassian/cssxp-types-support';

interface Props {
  params: SupportIssueSearchParams;
  showFilters?: boolean;
}

export const SupportIssuesCloud = React.memo(({ params, showFilters = false }: Props) => {
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });
  const [selectedStatus, setSelectedStatus] = useState<SupportIssueStatusEnum[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const cloudSiteId = decodeURIComponent(params?.cloudSiteId ?? '');
  const cloudSiteSen = decodeURIComponent(params?.sen ?? '');

  const { data, loading } = useQuery<FetchSearchSupportIssuesQuery, FetchSearchSupportIssuesQueryVariables>(
    fetchSearchSupportIssues,
    {
      variables: {
        searchParam: {
          cloudSiteId: cloudSiteId,
          sen: cloudSiteSen,
          updatedFrom: dateRange.from ? dateRange.from : null,
          updatedTo: dateRange.to ? dateRange.to : null,
          currentStatusIsIn: selectedStatus.length ? selectedStatus : null,
        },
        searchOptions: { first: rowsPerPage, offset: currentPage !== 1 ? currentPage * rowsPerPage - rowsPerPage : 0 },
      },
      displayName: `FetchCloudSupportIssueQuery`,
      skip: !params?.cloudSiteId,
    }
  );

  const supportIssuesConnection = data?.fetchSearchSupportIssues;
  const supportIssuesEdges = supportIssuesConnection?.edges || [];
  const totalHits = supportIssuesConnection?.totalCount ?? 0;

  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Issue key',
        width: 10,
      },
      {
        content: 'Summary',
        width: 40,
      },
      {
        content: 'Status',
        width: 15,
      },
      {
        content: 'Reporter',
        width: 25,
      },
      {
        content: 'Updated',
        width: 10,
      },
    ];

    return {
      cells,
    };
  }, []);

  const emptyStateOrderProp = {
    header: '',
    description: 'No support issues found',
  };

  const createRows = (): Array<any> => {
    const result = supportIssuesEdges.map((supportIssueEdge) => {
      const supportIssue = supportIssueEdge?.node;
      if (!supportIssue) {
        return null;
      }
      const { issueKey, updatedTimestamp, summary, reporterEmailAddress, currentStatus } = supportIssue;
      return {
        key: issueKey,
        cells: [
          {
            content: (
              <>
                {/*@ts-ignore*/}
                <img src={SupportIssueIcon} alt="support issue icon" className="support-icon" />
                <Link to={`${SUPPORT_ISSUE_PATH}/${issueKey}`} data-testid="support-issue-link" target="_blank">
                  {issueKey}
                </Link>
              </>
            ),
          },
          {
            content: summary,
          },
          {
            content: <SupportIssueStatusLozenge status={currentStatus} />,
          },
          {
            content: reporterEmailAddress,
          },
          {
            content: updatedTimestamp ? (
              <FormattedDate date={updatedTimestamp} format={NEW_DESIGN_DATE_FORMAT} />
            ) : (
              'N/A'
            ),
          },
        ],
      };
    });
    return result;
  };

  const getDateRange = useCallback((value: any) => {
    value.to = new Date(value.to / 1000).toISOString();
    value.from = new Date(value.from / 1000).toISOString();
    setDateRange(value);
  }, []);

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const onPageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const onStatusChange = useCallback((value: SupportIssueSelectedStatuses) => {
    setSelectedStatus(Object.keys(value) as SupportIssueStatusEnum[]);
  }, []);

  return (
    <div className="support-issues-cloud-list" data-testid={`cloud-support-issues-tab`}>
      <DynamicResultTable
        testId={`cloud-support-issues`}
        headingText="Support tickets"
        head={headData}
        rows={createRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        totalCount={totalHits}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={supportIssuesEdges && supportIssuesEdges?.length > 0 && totalHits > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        showFilters={showFilters}
      >
        <div className="support-filters">
          <SupportIssueStatusFilter onChange={onStatusChange} />
          <DateFilter dateValue={getDateRange} />
        </div>
      </DynamicResultTable>
    </div>
  );
});
